import React from "react";
import { CellProps, Column } from "react-table";
import Svg from "components/common/SVG/Svg";
import { Link } from "react-router-dom";
import { BsCheckLg, BsXLg, BsExclamationTriangle, BsPencilSquare } from "react-icons/bs";
import DataTableCheckbox from "components/common/DataTable/DataTableCheckbox";
import { IFeatureData } from "components/features/types";

const BsCheckLgIcon = BsCheckLg as any;
const BsXLgIcon = BsXLg as any;
const BsExclamationTriangleIcon = BsExclamationTriangle as any;
const BsPencilSquareIcon = BsPencilSquare as any;

type TProps = {
  handleCheckboxAction: (
    entity: IFeatureData,
    id: string,
    rowVersion: string,
    isEnabled: boolean,
  ) => Promise<void> | null;
  onEditConfig: (entity: IFeatureData) => void;
};

const FeaturesListManagementTableColumns = ({ handleCheckboxAction, onEditConfig }: TProps) => {
  const renderBoolean = (value: boolean) => (
    <span className={`small ${value ? "text-success" : "text-muted"}`}>{value ? "Yes" : "No"}</span>
  );

  return React.useMemo(
    () =>
      [
        {
          Header: "Name",
          accessor: "name",
          disableSortBy: true,
          Cell: ({ value }: any) => <span className="fw-medium text-dark">{value}</span>,
        },
        {
          Header: "Shared Config",
          accessor: "capabilities",
          disableSortBy: true,
          Cell: ({ value }: any) => renderBoolean(value?.includes("CanUseSharedConfigurations")),
        },
        {
          Header: "Schema",
          accessor: "configurationSchema",
          disableSortBy: true,
          Cell: ({ value }: any) => renderBoolean(!!value && Object.keys(value).length > 0),
        },
        {
          Header: "Actions",
          accessor: "actions",
          disableSortBy: true,
          Cell: ({ row: { original } }: any) =>
            original?.capabilities?.includes("CanUseSharedConfigurations") && (
              <Link to={`/host/features/${original.featureId}`} className="btn btn-link p-0">
                {Svg({ icon: "arrowRight", width: 20, height: 20 })}
              </Link>
            ),
        },
        {
          Header: "Config",
          accessor: "adminConfig",
          disableSortBy: true,
          Cell: ({ row: { original } }: { row: { original: IFeatureData } }) => {
            const hasSharedConfig = original?.capabilities?.includes("CanUseSharedConfigurations");
            const isConfigured = original?.configuration;
            const renderEditButton = (
              <button
                type="button"
                className="btn-nulled ms-2 p-0 border-0 bg-transparent"
                aria-label="Edit configuration"
                title="Edit configuration"
                onClick={() => onEditConfig(original)}
              >
                <BsPencilSquareIcon size="15px" />
              </button>
            );

            return (
              <span className="d-inline-flex align-items-center gap-1">
                {isConfigured ? (
                  <>
                    <BsCheckLgIcon size="1em" className="text-success" />
                    Configured
                  </>
                ) : (
                  <>
                    <BsXLgIcon size="1em" className="text-danger" />
                    No configured
                  </>
                )}
                {!hasSharedConfig && renderEditButton}
              </span>
            );
          },
        },
        {
          Header: "Config",
          accessor: "config",
          disableSortBy: true,
          Cell: ({ row: { original } }: { row: { original: IFeatureData } }) => {
            const hasSharedConfig = original?.capabilities?.includes("CanUseSharedConfigurations");
            const isConfigured = original?.configId;

            const renderEditButton = (
              <button
                type="button"
                className="btn-nulled ms-2 p-0 border-0 bg-transparent"
                aria-label="Edit configuration"
                title="Edit configuration"
                onClick={() => onEditConfig(original)}
              >
                <BsPencilSquareIcon size="15px" />
              </button>
            );

            if (!hasSharedConfig) {
              return (
                <span className="d-inline-flex align-items-center gap-1">
                  <BsExclamationTriangleIcon size="1em" className="text-warning" />
                  By tenant
                </span>
              );
            }

            return (
              <span className="d-inline-flex align-items-center gap-1">
                {isConfigured ? (
                  <>
                    <BsCheckLgIcon size="1em" className="text-success" />
                    {original.configName} Configured
                  </>
                ) : (
                  <>
                    <BsXLgIcon size="1em" className="text-danger" />
                    No config
                  </>
                )}
                {renderEditButton}
              </span>
            );
          },
        },
        {
          Header: "Active",
          accessor: "active",
          disableSortBy: true,
          Cell: ({ row: { original } }: CellProps<any>) => {
            const hasSharedConfig = original?.capabilities?.includes("CanUseSharedConfigurations");
            const isConfigured = original?.configId;
            const entity = original as IFeatureData;

            return (
              <DataTableCheckbox
                {...{
                  checked: original?.isEnabled,
                  onChange: () => {
                    handleCheckboxAction(
                      entity,
                      original.id,
                      original.rowVersion,
                      original.isEnabled,
                    );
                  },
                  disabled: hasSharedConfig && !isConfigured,
                  title: hasSharedConfig && !isConfigured ? "You need config before active" : "",
                }}
              />
            );
          },
        },
      ] as Column<object>[],
    [handleCheckboxAction, onEditConfig],
  );
};

export default FeaturesListManagementTableColumns;
